import { Directive, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, Subscription, catchError, debounceTime, of, switchMap, tap } from 'rxjs';
import { SimpleChangesTyped, isRealChange } from '../../helper/simple-changes-typed';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { FormControlEx } from '../forms/form-control';
import { InputControlBase } from '../forms/input-control.base';
import { AutoCompleteSelectionItem } from './autocomplete-selection-item';
import { IAutoCompleteService } from './autocomplete-service.interface';

@Directive({
    standalone: false
})
export class AutocompleteRemoteBase<TData = any, TOptions = any> extends InputControlBase<string> implements OnChanges {
  constructor(
    protected readonly fcf: FormControlExFactory,
    protected readonly isPaged: boolean
  ) {
    super();
  }

  /**
   * This is the initially text shown in the selector field pre-fetch.
   * It will be replaced once the value loading is finished, so it can be used to show a 'loading...' text,
   * or even better, if the name of the value being loaded is already known, it can (and should) show that.
   * */
  @Input({ alias: 'init-text' }) initialText: string;
  @Input() service: IAutoCompleteService<TData, TOptions>;
  @Input() defaultSearch: string;
  @Input() serviceOptions: TOptions;
  @Input() clearOthers: boolean;
  @Input() filterActions = false;
  @Input() updateOnOptionsChange: boolean = false;
  @Output() addonClick = new EventEmitter<FormControlEx>();
  @Output() clearClick = new EventEmitter<FormControlEx>();
  @Output() itemSelected = new EventEmitter<AutoCompleteSelectionItem<TData>>();
  @Output() pageLoaded = new EventEmitter<number>();

  protected selectionControl: FormControl<string>;
  protected itemsSource: AutoCompleteSelectionItem<TData>[];
  private _selectedItem: AutoCompleteSelectionItem<TData>;

  protected page: number = 0;
  protected loadPageSub: Subscription;
  protected allPagesLoaded: boolean = false;
  protected isSearchLoading = false;

  protected get selectedItem(): AutoCompleteSelectionItem<TData> {
    return this._selectedItem;
  }
  protected set selectedItem(item: AutoCompleteSelectionItem<TData>) {
    if (item && this._selectedItem && item.value === this._selectedItem.value) {
      return;
    }
    if (!item && !this._selectedItem) {
      return;
    }
    this._selectedItem = item;
    this.itemSelected.emit(item);
  }

  protected get isReadonly(): boolean {
    return this.control?.isReadonly;
  }

  get hasSearchQuery(): boolean {
    return !!this.selectionControl?.value?.trim();
  }

  get noItems(): boolean {
    return this.hasSearchQuery && !this.isSearchLoading && this.allPagesLoaded && !this.itemsSource?.length;
  }

  get hasPanelContent(): boolean {
    return !!this.itemsSource?.length || this.noItems;
  }

  get showClearOthers(): boolean {
    return (
      this.clearOthers &&
      Object.keys(this.control.parent.controls).length > 1 &&
      (this.filterActions || !!this.control.value || (this.control.value as unknown as number) === 0)
    ); // TODO: is there actually a real use-case where "TValue" is number..?
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.loadPageSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChangesTyped<AutocompleteRemoteBase>): void {
    if (isRealChange(changes.serviceOptions)) {
      console.debug('ac:options-changes');
      if (this.updateOnOptionsChange) {
        this.updatingOptions();
      } else if (this.selectedItem) {
        // Realistically speaking it would probably make sense to re-fetch the selected item,
        // but that could probably be a break change to existing component setups, so we dont.
        // But we CAN safely and defensively purge all cached items except the selected one.
        this.itemsSource = [this.selectedItem];
      }
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.isReadonly) {
      console.debug('ac:readonly');
    }

    this.selectionControl = this.fcf.createWithOptions(this.control.caption, this.initialText || null, {
      asyncValidator: this.control.asyncValidator,
      validator: this.control.asyncValidator,
      helpText: this.control.helpText?.toString() || null,
      isReadonly: this.control.isReadonly,
      required: this.control.required,
    });

    this.subscription.add(
      this.selectionControl.valueChanges.subscribe(search => {
        this.isSearchLoading = !!search?.trim();
      })
    );
    this.subscription.add(this.selectionControl.valueChanges.pipe(debounceTime(400)).subscribe(search => this.startNewSearch(search)));

    if (this.control.value !== undefined && this.control.value !== null && this.control.value !== '') {
      this.subscription.add(
        this.getItemByValue(this.control.value, false).subscribe(item => {
          if (item) this.syncLocalStateToItem(item);
        })
      );
    } else {
      this.startNewSearch();
    }

    this.subscription.add(
      this.control.valueChanges.pipe(switchMap(value => this.getItemByValue(value, false))).subscribe(item => {
        this.syncLocalStateToItem(item);
        if (!item) {
          this.itemsSource = []; // <-- must clear the item cache if source value cleared.
        }
      })
    );

    this.subscription.add(
      this.control.touchStateChanged.subscribe(touched =>
        touched ? this.selectionControl.markAsTouched() : this.selectionControl.markAsUntouched()
      )
    );
  }

  onBlur(event: FocusEvent) {
    console.debug('ac:blurto', event?.relatedTarget);
    if (this.selectionControl.value) {
      if (this.itemsSource?.length === 1) {
        this.setControlValuePrivate(this.itemsSource[0]);
      } else {
        const isOptionSelect = (event?.relatedTarget as HTMLElement)?.tagName === 'MAT-OPTION';
        // onBlur is called BEFORE onOptionSelected :(
        // This means that if we modify the selectionControl during onBlur it can prevent onOptionSelected from firing.
        // Only update local state if the user is shifting focus to something OTHER than a <mat-option> element:
        if (!isOptionSelect && this.selectedItem) {
          this.setControlValuePrivate(this.selectedItem);
        }
      }
    } else {
      this.setControlValuePrivate(null);
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    console.debug('ac:select', this.selectionControl.value);
    const item = this.findCachedItemByName(this.selectionControl.value);
    this.setControlValuePrivate(item);
  }

  addonClicked(): boolean {
    for (let controlName of Object.keys(this.control.parent.controls)) {
      const currentControl = this.control.parent.get(controlName);
      if (currentControl !== this.control && !(currentControl as FormControlEx).isReadonly) {
        currentControl.setValue(undefined);
      }
    }
    this.addonClick.emit(this.control);
    return false;
  }

  clearBtnClicked(): boolean {
    if (!this.isReadonly) {
      this.control.setValue(undefined);
      this.selectionControl.setValue(undefined);
      this.control.markAsDirty();
    }
    this.clearClick.emit(this.control);
    return false;
  }

  startNewSearch(search?: string): void {
    search = search || this.defaultSearch;
    if (search) {
      this.cancelLoadPage();
      this.allPagesLoaded = false;
      this.isSearchLoading = true;
      if (!this.findCachedItemByName(search)) {
        const page = this.isPaged ? 1 : undefined;
        this.loadPage(search, page);
      } else {
        this.isSearchLoading = false;
      }
    } else {
      this.isSearchLoading = false;
    }
  }

  // updates search result when this.serviceOptions changes
  updatingOptions() {
    if (!this.serviceOptions) {
      console.debug('ac:options-updating');
      this.startNewSearch();
    }
    const value = this.control.value;
    if (!value) {
      console.debug('ac:options-clearing');
      this.clearBtnClicked();
      return;
    }
    console.debug('ac:verifying', value, this.serviceOptions);
    this.getItemByValue(value, true)
      .pipe(
        tap(item => {
          if (item) {
            // current selection (probably) valid - refresh the itemsSource only (keep current selection):
            console.debug('ac:options-updating');
            this.startNewSearch();
          } else {
            // current selection is invalid - clear selection (implicit itemSource refresh):
            console.debug('ac:options-clearing');
            this.clearBtnClicked();
          }
        }),
        catchError(error => {
          console.warn(error);
          return [];
        })
      )
      .subscribe();
  }

  protected cancelLoadPage() {
    this.loadPageSub?.unsubscribe();
  }

  protected loadPage(search: string, page?: number): void {
    console.debug('ac:loadPage', page);
    const observable = this.service.getSelection(search, this.serviceOptions, page);
    this.loadPageSub = observable
      .pipe(
        tap(items => {
          if (!page || page === 1 || !this.itemsSource) {
            this.itemsSource = items;
          } else {
            this.itemsSource.push(...items);
          }
          this.page = page; // Update this.page *after* itemsSource has been updated.
          // So in case page-load is canceled before the service responds, we will re-try the same page next if "nextPage" is called.
          if (!items.length) {
            this.allPagesLoaded = true;
          }
          this.isSearchLoading = false;
          this.pageLoaded.emit(page || 0);
        }),
        catchError(error => {
          console.warn(error);
          this.isSearchLoading = false;
          return [];
        })
      )
      .subscribe();
  }

  protected nextPage() {
    if (!this.page) return;
    const search = this.selectionControl.value || this.defaultSearch;
    this.loadPage(search, this.page + 1);
  }

  // need this because the protected one is overridden in the child classes
  private setControlValuePrivate(item: AutoCompleteSelectionItem<TData>): void {
    if (!this.isReadonly) {
      this.setControlValue(item);
    }
  }

  protected setControlValue(item: AutoCompleteSelectionItem<TData>): void {
    if (this.isReadonly) {
      return;
    }
    const cValue = this.control.value;
    if (!item && !cValue) {
      return;
    }
    if (item && cValue && cValue === item.value) {
      // Whenever an attempt at assigning a value to the source is made we must always ensure that
      // ALL of our local state is re-synced to that value. Local state de-syncs from the source state
      // whenever the user engages with the autocomplete search, so a re-sync is always needed after.
      // It is NOT enough to only do this in "this.control.valueChanged" because in some cases the
      // source value doesn't actually need to change (exactly what the above if-statement is saying).
      // Thus we have to sync local state here in this method too.
      // Example: user begins an autocomplete search, but blurs the control without selecting an item.
      this.syncLocalStateToItem(item);
      return;
    }
    this.control.setValue(item?.value || null);
    this.control.markAsDirty();
  }

  protected syncLocalStateToItem(item: AutoCompleteSelectionItem<TData>): void {
    console.debug('ac:syncto', item);
    if (!item) {
      this.selectionControl.setValue(null, { emitEvent: false });
      this.selectedItem = null;
    } else {
      if (!this.itemsSource?.some(x => x.value === item.value)) {
        this.itemsSource = [item];
      }
      const name = item.name ?? item.shortName;
      if (name && name !== this.selectionControl.value) {
        this.selectionControl.setValue(name, { emitEvent: false });
      }
      this.selectedItem = item;
    }
  }

  protected getItemByValue(value: string, isOptionChange: boolean): Observable<AutoCompleteSelectionItem<TData>> {
    if (value === undefined || value === null) {
      return of(null as AutoCompleteSelectionItem<TData>);
    }
    this.cancelLoadPage(); // <-- stop any ongoing load that started before the source control changed its value.
    if (!isOptionChange) {
      // <-- if serviceOption is unchanged, we can use the cached items.
      if (this.selectedItem && this.selectedItem.value === value) {
        return of(this.selectedItem);
      }
      const selectedItem = this.itemsSource?.find(item => item.value === value) || null;
      if (selectedItem) {
        return of(selectedItem);
      }
    }
    return this.service.getSelectionItem(value, { selectedItem: null, options: this.serviceOptions });
  }

  // returns undefined if not found
  protected findCachedItemByName(search: string): AutoCompleteSelectionItem<TData> {
    const item = this.itemsSource?.find(x => (x.shortName && x.shortName) === search || (x.name && x.name === search));
    return item;
  }
}
